import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CalibrationCoachGate from '../coach/CalibrationCoachGate'
import AgentDecisionDetailSheet from '../coach/AgentDecisionDetailSheet'
import AgentSuggestionCard from '../coach/AgentSuggestionCard'
import CoachInterventionsCard from '../coach/CoachInterventionsCard'
import ActiveCalendarCard from '../coach/ActiveCalendarCard'
import TrackingProgressCard from '../coach/TrackingProgressCard'
import CoachBriefingsCard from '../coach/CoachBriefingsCard'
import CoachWorkoutSection from '../coach/CoachWorkoutSection'
import CoachBodyCard from '../coach/CoachBodyCard'
import DeviceConnect from '../coach/DeviceConnect'
import UrgentConflictBanner from '../coach/UrgentConflictBanner'
import ProUpgradeCard from '../coach/ProUpgradeCard'
import LiveWorkoutSession from '../workout/LiveWorkoutSession'
import { useAgentSuggestions } from '../../hooks/useAgentSuggestions'
import { useCoachHealthSummary } from '../../lib/useCoachHealthSummary'
import { KILO } from '../../constants/KILO_THEME'
import type { CycleProfile } from '../../lib/cycle'
import type { ConflictAlert, SuggestedSlot } from '../../lib/conflicts'
import type { GroceryItem, MealBlock, TrainingBlock } from '../../lib/protocolTypes'

type CoachTabProps = {
  training: TrainingBlock[]
  isPremium: boolean
  canWearables: boolean
  canPredictiveCoach: boolean
  canCalendarSync: boolean
  canWeeklyCalendarView: boolean
  userId?: string | null
  bodyWeightKg?: number | null
  cycleProfile?: CycleProfile | null
  calendarSyncEnabled?: boolean
  dietaryLabel?: string
  meals?: MealBlock[]
  grocery?: GroceryItem[]
  conflict?: ConflictAlert | null
  onOpenWeeklyView?: () => void
  onOpenShopping?: () => void
  onOpenMeals?: () => void
  onWorkoutUpdated?: () => void
  onConnectCalendar?: () => void
  onUpgrade?: () => void
  onRescheduleConflict?: (slot: SuggestedSlot) => Promise<void>
  onDismissConflict?: () => Promise<void>
}

const CoachTab = ({
  onConnectCalendar,
  onUpgrade,
  conflict,
  onRescheduleConflict,
  onDismissConflict,
  canWearables,
  canPredictiveCoach,
  canCalendarSync,
  canWeeklyCalendarView,
  meals = [],
  ...props
}: CoachTabProps) => {
  const { i18n, t } = useTranslation()
  const [liveOpen, setLiveOpen] = useState(false)
  const [briefingsOpen, setBriefingsOpen] = useState(false)
  const [detailDecisionId, setDetailDecisionId] = useState<string | null>(null)
  const { userId, onWorkoutUpdated } = props
  const { suggestions, accept, skip } = useAgentSuggestions({ userId, enabled: canWearables })
  const { summary, loading, connecting, setConnecting, fetchSummary } = useCoachHealthSummary({
    enabled: canWearables,
  })

  const headerDate = useMemo(() => {
    const now = new Date()
    return {
      weekday: now.toLocaleDateString(i18n.language, { weekday: 'long' }),
      date: now.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
    }
  }, [i18n.language])

  const showUrgentConflict = canCalendarSync && Boolean(conflict)
  const showActiveCalendarEducation =
    Boolean(summary?.calendar_stress_context) && !summary?.calendar_stress_context?.calendar_connected

  const proUpgradeFeatures: Array<'wearables' | 'predictive' | 'calendar'> = []
  if (!canWearables && onUpgrade) proUpgradeFeatures.push('wearables')
  if (!canPredictiveCoach && onUpgrade) proUpgradeFeatures.push('predictive')
  if (!canCalendarSync && onUpgrade) proUpgradeFeatures.push('calendar')

  return (
    <div key={i18n.language} className="flex flex-col gap-4 pb-24">
      <header className={KILO.stickyHeader}>
        <p className={KILO.meta}>{t('coach.header.meta')}</p>
        <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{t('coach.header.title')}</h1>
        <p className={`mt-1 text-sm ${KILO.text.soft}`}>
          {t('coach.header.subtitle', headerDate)}
        </p>
      </header>

      {showUrgentConflict && conflict ? (
        <UrgentConflictBanner alert={conflict} onTap={() => setBriefingsOpen(true)} />
      ) : null}

      {canWearables && !loading && !connecting && summary && !summary.has_device_connected ? (
        <DeviceConnect variant="banner" dismissible onConnecting={() => setConnecting(true)} />
      ) : null}

      {canPredictiveCoach ? <CoachInterventionsCard /> : null}

      {canWearables && suggestions.length > 0 ? (
        <AgentSuggestionCard
          decisions={suggestions}
          onAccept={accept}
          onSkip={skip}
          onTellMeMore={(id) => setDetailDecisionId(id)}
        />
      ) : null}

      <AgentDecisionDetailSheet
        decision={detailDecisionId ? suggestions.find((d) => d.id === detailDecisionId) ?? null : null}
        open={detailDecisionId != null}
        onClose={() => setDetailDecisionId(null)}
        onAccept={accept}
        onSkip={skip}
      />

      {canWearables && showActiveCalendarEducation ? (
        <ActiveCalendarCard
          onConnect={onConnectCalendar}
          unlockHint={summary?.calendar_stress_context?.unlock_hint}
        />
      ) : null}

      {canWearables ? (
        <CoachBriefingsCard
          summary={summary}
          loading={loading}
          conflict={canCalendarSync ? conflict : null}
          forceOpen={briefingsOpen}
          onOpenChange={setBriefingsOpen}
          onRescheduleConflict={onRescheduleConflict}
          onDismissConflict={onDismissConflict}
        />
      ) : null}

      {canWearables ? (
        <TrackingProgressCard summary={summary} loading={loading} userId={userId} meals={meals} />
      ) : null}

      <CalibrationCoachGate
        userId={userId ?? null}
        bodyWeightKg={props.bodyWeightKg}
        onProtocolRefresh={onWorkoutUpdated}
        onOpenShopping={props.onOpenShopping}
      >
        <CoachWorkoutSection
          {...props}
          canWeeklyCalendarView={canWeeklyCalendarView}
          hasWearableConnected={canWearables ? Boolean(summary?.has_device_connected) : false}
          showLiveSession={Boolean(userId && canWearables)}
          onLiveSession={() => setLiveOpen(true)}
          onWorkoutUpdated={onWorkoutUpdated}
        />
      </CalibrationCoachGate>

      {canWearables ? (
        <CoachBodyCard
          summary={summary}
          loading={loading}
          connecting={connecting}
          onConnecting={() => setConnecting(true)}
          onRefresh={fetchSummary}
        />
      ) : null}

      {proUpgradeFeatures.map((feature) => (
        <ProUpgradeCard key={feature} feature={feature} onUpgrade={onUpgrade!} dismissible />
      ))}

      {liveOpen && userId ? (
        <LiveWorkoutSession
          userId={userId}
          canWearables={canWearables}
          onUpgrade={onUpgrade}
          onClose={() => setLiveOpen(false)}
          onSessionFinalized={onWorkoutUpdated}
        />
      ) : null}
    </div>
  )
}

export default CoachTab
